<template>
  <div class="login-container">
    <div class="login-wrapper">
      <!-- Left Side - Branding & Info -->
      <div class="login-left">
        <div class="brand-section">
          <div class="brand-logo mb-4">
            <i class="bi bi-mortarboard-fill"></i>
          </div>
          <h1 class="brand-title mb-3">LifeSkill</h1>
          <p class="brand-subtitle mb-5">Empowering children through interactive learning experiences</p>
          
          <div class="features-list">
            <div class="feature-item">
              <i class="bi bi-star-fill"></i>
              <span>Gamified Learning</span>
            </div>
            <div class="feature-item">
              <i class="bi bi-people-fill"></i>
              <span>Multi-Role Platform</span>
            </div>
            <div class="feature-item">
              <i class="bi bi-shield-check"></i>
              <span>Safe & Secure</span>
            </div>
            <div class="feature-item">
              <i class="bi bi-graph-up"></i>
              <span>Progress Tracking</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Login Form -->
      <div class="login-right">
        <div class="login-form-container">
          <div class="text-center mb-4">
            <h2 class="form-title mb-2">Welcome Back!</h2>
            <p class="form-subtitle">Choose your role to continue</p>
          </div>

          <!-- Role Selection -->
          <div class="role-selector mb-4">
            <div 
              v-for="role in roles" 
              :key="role.value"
              class="role-option"
              :class="{ active: selectedRole === role.value }"
              @click="selectedRole = role.value"
            >
              <div class="role-icon" :style="{ background: role.value === 'student' ? '#10b981' : role.value === 'parent' ? '#f59e0b' : '#06b6d4' }">
                <i :class="role.icon"></i>
              </div>
              <div class="role-info">
                <h6 class="role-title">{{ role.label }}</h6>
                <small class="role-desc">{{ role.subtitle }}</small>
              </div>
              <div class="role-check">
                <i class="bi bi-check-circle-fill" v-if="selectedRole === role.value"></i>
                <i class="bi bi-circle" v-else></i>
              </div>
            </div>
          </div>

          <!-- Login Form -->
          <form @submit.prevent="handleLogin" class="login-form">
            <div class="form-group mb-3">
              <label for="email" class="form-label">Email Address</label>
              <div class="input-with-icon">
                <i class="bi bi-envelope"></i>
                 <input
                  type="email"
                  class="form-control"
                  id="email"
                  v-model="loginForm.email"
                   placeholder="Enter your email"
                  required
                />
              </div>
            </div>
            
            <div class="form-group mb-3">
              <label for="password" class="form-label">Password</label>
              <div class="input-with-icon">
                <i class="bi bi-lock"></i>
                 <input
                  type="password"
                  class="form-control"
                  id="password"
                  v-model="loginForm.password"
                   placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <!-- Forgot Password Link -->
            <div class="text-end mb-4">
              <router-link to="/reset-password" class="forgot-password-link">
                <i class="bi bi-key me-1"></i>
                Forgot your password?
              </router-link>
            </div>

            <button 
              type="submit" 
              class="btn btn-login w-100 mb-3"
              :disabled="isLoading"
              :style="{ background: selectedRoleData.bgGradient }"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
              <i :class="selectedRoleData.icon" class="me-2"></i>
              Sign In as {{ selectedRoleData.label }}
            </button>

            <!-- Divider -->
            <div class="divider">
              <span class="divider-text">or</span>
            </div>

            <!-- Google Sign-In Button -->
            <GoogleSignIn 
              :role="selectedRole"
              :button-text="`Sign in with Google`"
              :disabled="isLoading"
              @success="handleGoogleSignInSuccess"
              @error="handleGoogleSignInError"
              @loading="handleGoogleLoading"
            />

            


          </form>

          <!-- Error Message -->
          <div v-if="errorMessage" class="alert alert-danger mt-3">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="alert alert-success mt-3">
            <i class="bi bi-check-circle me-2"></i>
            {{ successMessage }}
          </div>

          <!-- Registration Link -->
          <div class="text-center mt-4">
            <div class="divider mb-3">
              <span class="divider-text">New to LifeSkill?</span>
            </div>
            <button 
              class="btn btn-register w-100"
              @click="$router.push('/register')"
            >
              <i class="bi bi-person-plus me-2"></i>
              Create Your Account
            </button>
            <p class="register-benefits mt-2">
              <small class="text-muted">
                Join thousands of learners and start your journey today!
              </small>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService, { loginUser } from '../services/authService.js'
import GoogleSignIn from './GoogleSignIn.vue'

export default {
  name: 'LoginPage',
  components: {
    GoogleSignIn
  },
  data() {
    return {
      selectedRole: 'student',
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      loginForm: {
        email: '',
        password: ''
      },
      roles: [
        {
          value: 'student',
          label: 'Student',
          icon: 'bi-person-fill',
          email: '',
          password: '',
          title: 'Student Portal',
          subtitle: 'Learn, grow, and achieve your goals',
          bgGradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          features: [
            'Interactive skill modules',
            'Progress tracking & badges',
            'Reading stories & activities',
            'Emotion check-ins'
          ]
        },
        {
          value: 'parent',
          label: 'Parent',
          icon: 'bi-people-fill',
          email: '',
          password: '',
          title: 'Parent Dashboard',
          subtitle: 'Monitor your child\'s progress',
          bgGradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
          features: [
            'Child progress monitoring',
            'Activity reports & insights',
            'Communication with teachers',
            'Reward system management'
          ]
        },
        {
          value: 'teacher',
          label: 'Teacher',
          icon: 'bi-mortarboard-fill',
          email: '',
          password: '',
          title: 'Teacher Hub',
          subtitle: 'Manage classes and student progress',
          bgGradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
          features: [
            'Class management tools',
            'Student progress analytics',
            'Assignment & module creation',
            'Parent communication portal'
          ]
        }
      ]
    }
  },
  computed: {
    selectedRoleData() {
      return this.roles.find(role => role.value === this.selectedRole) || this.roles[0]
    }
  },
  watch: {
    selectedRole() {
      // no auto fill; keep user-entered email
    }
  },
  mounted() {
    // No autofill for production testing
  },

  methods: {
    async handleLogin() {
      try {
        this.isLoading = true
        this.errorMessage = ''
        this.successMessage = ''
        
        const user = await loginUser(this.loginForm.email, this.loginForm.password)
        
        // Check if email verification is required
        if (user.emailVerified === false) {
          this.successMessage = 'Please verify your email address before signing in. Check your inbox for verification link.'
          this.$router.push('/verify-email')
          return
        }
        
        // Save user to localStorage for persistence
        localStorage.setItem('currentUser', JSON.stringify(user))
        
        this.successMessage = `Welcome back, ${user.name}!`
        
        // Small delay to show success message
        setTimeout(() => {
          // Redirect to appropriate dashboard
          switch (user.role) {
            case 'student':
              this.$router.push('/student-dashboard')
              break
            case 'parent':
              this.$router.push('/parent-dashboard')
              break
            case 'teacher':
              this.$router.push('/teacher-dashboard')
              break
            default:
              this.$router.push('/student-dashboard')
          }
        }, 1000)
        
      } catch (error) {
        this.errorMessage = error.message
        
        // Show specific guidance based on error type
        if (error.message.includes('locked')) {
          this.errorMessage += ' Please try again later or reset your password.'
        } else if (error.message.includes('attempts remaining')) {
          this.errorMessage += ' Please double-check your credentials.'
        }
      } finally {
        this.isLoading = false
      }
    },

    // Google Sign-In event handlers
    handleGoogleSignInSuccess(result) {
      // Persist token + user from social login
      const user = authService.persistSocialLogin(result)
      if (!user) {
        this.errorMessage = 'Login completed but token missing. Please try again.'
        return
      }
      this.successMessage = `Welcome, ${user.name}!`

      setTimeout(() => {
        switch (user.role) {
          case 'student':
            this.$router.push('/student-dashboard')
            break
          case 'parent':
            this.$router.push('/parent-dashboard')
            break
          case 'teacher':
            this.$router.push('/teacher-dashboard')
            break
          default:
            this.$router.push('/student-dashboard')
        }
      }, 800)
    },

    handleGoogleSignInError(error) {
      // Handle Google sign-in error
      this.errorMessage = error.message || 'Google sign-in failed. Please try again.'
    },

    handleGoogleLoading(isLoading) {
      // Handle loading state from Google Sign-In
      this.isLoading = isLoading
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.login-wrapper {
  display: flex;
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  max-width: 900px;
  width: 100%;
  min-height: 600px;
}

/* Left Side - Branding */
.login-left {
  flex: 1;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #06b6d4 100%);
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  pointer-events: none;
}

.brand-section {
  position: relative;
  z-index: 1;
}

.brand-logo {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  backdrop-filter: blur(10px);
}

.brand-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin: 0;
}

.brand-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.95);
}

.feature-item i {
  width: 24px;
  font-size: 1.1rem;
  color: #fbbf24;
}

/* Right Side - Form */
.login-right {
  flex: 1;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.login-form-container {
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
}

.form-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.form-subtitle {
  color: #6b7280;
  font-size: 1rem;
}

/* Role Selection */
.role-selector {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.role-option {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.role-option:hover {
  border-color: #d1d5db;
  background: #f3f4f6;
}

.role-option.active {
  border-color: #3b82f6;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.role-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  margin-right: 0.75rem;
}

.role-info {
  flex: 1;
}

.role-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.role-desc {
  color: #6b7280;
  font-size: 0.8rem;
}

.role-check {
  color: #3b82f6;
  font-size: 1.2rem;
}

/* Form Styling */
.form-group {
  margin-bottom: 1rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.input-with-icon {
  position: relative;
}

.input-with-icon i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 1rem;
}

.input-with-icon .form-control {
  padding-left: 2.75rem;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.input-with-icon .form-control:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-login {
  height: 48px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  color: white;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-login:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

/* Demo Info */
.demo-info {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
}

.demo-badge {
  display: inline-flex;
  align-items: center;
  background: #3b82f6;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.demo-text {
  color: #6b7280;
  font-size: 0.85rem;
  margin: 0;
}



/* Registration Section */
.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
  z-index: 1;
}

.divider-text {
  position: relative;
  background: white;
  padding: 0 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 2;
}

.btn-register {
  height: 48px;
  border: 2px solid #10b981;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #10b981;
  background: white;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-register:hover {
  background: #10b981;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.2);
}

.register-benefits {
  margin-top: 0.5rem;
}

/* Forgot Password Link */
.forgot-password-link {
  color: var(--role-primary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.3s ease;
}

.forgot-password-link:hover {
  color: var(--role-primary-dark);
  text-decoration: underline;
}

/* Alert Styles */
.alert {
  border: none;
  border-radius: 15px;
  padding: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.alert-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.alert-success {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

/* Responsive Design */
@media (max-width: 768px) {
  .login-wrapper {
    flex-direction: column;
    max-width: 100%;
    margin: 1rem;
    min-height: auto;
  }
  
  .login-left {
    padding: 2rem;
    text-align: center;
  }
  
  .login-right {
    padding: 2rem;
  }
  
  .brand-title {
    font-size: 2rem;
  }
  
  .features-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
}
</style> 